import React, {
  memo,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { DragDropContext } from "react-beautiful-dnd";
import { Slide as ToastSlide, toast } from "react-toastify";
import moment from "moment";
import cx from "classnames";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";

// @material-ui/icons
import { PostAdd, OpenInBrowser, DirectionsWalk } from "@material-ui/icons";

// core components
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import Button from "components/CustomButtons/Button";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";

// Initiative components
import DroppableStageColumn from "../../../components/Initiatives/DroppableStageColumn";
import InitiativesTimeline from "../../../components/Initiatives/InitiativesTimeline";
import AddUpdateDialog from "../../../components/Initiatives/AddUpdateDialog";
import KeyMessagesPanel from "./KeyMessagesPanel";
import FocusModeWrapper from "./FocusModeWrapper";

// Modal styles
import modalStyle from "../../../assets/jss/material-dashboard-pro-react/modalStyle";

// Services
import { client } from "../../../services/axiosClient";

// Hooks
import useThemeColors from "../../../hooks/useThemeColors";

// Styles
import {
  grayColor,
  blendColors,
} from "../../../assets/jss/material-dashboard-pro-react";

const useStyles = makeStyles((theme) => ({
  ...modalStyle(theme),
  sectionContainer: {
    marginTop: "30px",
  },
  sectionContainerFocus: {
    // marginTop handled by FocusModeWrapper
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  card: {
    height: "calc(100vh - 176px)",
    marginBottom: 0,
  },
  cardFocus: {
    flex: 1,
    marginBottom: 0,
    marginTop: 0, // Override Card base style
    display: "flex",
    flexDirection: "column",
    maxHeight: "100%",
  },
  cardBody: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    padding: "8px",
    overflow: "hidden",
  },
  boardContainer: {
    height: "45%",
    overflow: "hidden",
  },
  boardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "8px",
    height: "100%",
  },
  detailsContainer: {
    height: "55%",
    display: "flex",
    flexDirection: "column",
    borderTop: `1px solid ${grayColor[10]}`,
    paddingTop: "8px",
    marginTop: "8px",
    overflow: "hidden",
  },
  detailsContent: {
    display: "flex",
    gap: "10px",
    height: "100%",
    overflow: "hidden",
  },
  detailsLeftPanel: {
    flex: "1 1 50%",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  detailsRightPanel: {
    flex: "1 1 50%",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  noSelectionMessage: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: grayColor[3],
    fontSize: "14px",
    fontStyle: "italic",
  },
  // Details card styles (matching original)
  detailsCard: {
    marginTop: "0",
    marginBottom: "8px",
  },
  cardCategory: {
    color: theme.palette.custom?.clientTextColor || "#ffffff",
    fontSize: "14px",
    paddingTop: "5px",
    marginBottom: "0",
    marginTop: "0",
    margin: "0",
    fontWeight: "400",
    textTransform: "uppercase",
  },
  cardButton: {
    padding: "0 !important",
    margin: "5px 0 0 5px !important",
    height: "22px !important",
    width: "30px !important",
    minWidth: "0 !important",
    position: "relative",
    transition: "all 0.2s",
  },
  cardButtonIcon: {
    margin: "0 !important",
    position: "absolute !important",
    top: "50% !important",
    left: "50% !important",
    transform: "translate(-50%, -50%)",
    width: "18px !important",
    height: "18px !important",
  },
  cardButtonDisabled: {
    "& svg, & .MuiSvgIcon-root": {
      opacity: "0.65 !important",
      color: "rgba(255, 255, 255, 0.65) !important",
    },
  },
  // Row layout (15%/85% like original)
  rowContainer: {
    display: "flex",
    marginBottom: "6px",
    flex: 1,
    minHeight: 0,
  },
  titleCard: {
    width: "15%",
    marginRight: "8px",
  },
  titleCardInner: {
    color: grayColor[3],
    backgroundColor: grayColor[8] + "44",
    textAlign: "center",
    padding: "8px 6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    fontSize: "14px",
  },
  contentCard: {
    width: "85%",
  },
  contentCardBody: {
    padding: "8px 10px",
  },
  // Read-only field styles
  fieldLabel: {
    fontSize: "14px",
    color: grayColor[3],
    marginBottom: "2px",
  },
  fieldValue: {
    fontSize: "14px",
    color: grayColor[1],
    fontWeight: 300,
    borderBottom: `1px solid ${grayColor[10]}`,
    paddingBottom: "4px",
    minHeight: "22px",
  },
  fieldValueBold: {
    fontWeight: 400,
  },
  scopeChip: {
    display: "inline-block",
    padding: "3px 10px",
    marginRight: "4px",
    marginBottom: "2px",
    backgroundColor: grayColor[12],
    borderRadius: "4px",
    fontSize: "14px",
    color: grayColor[1],
  },
  tagChip: {
    display: "inline-block",
    padding: "3px 10px",
    marginRight: "4px",
    marginBottom: "2px",
    backgroundColor: grayColor[10],
    borderRadius: "12px",
    fontSize: "14px",
    color: grayColor[2],
  },
  noValueText: {
    fontSize: "14px",
    color: grayColor[6],
    fontStyle: "italic",
  },
  // Details panel wrapper to fill vertical space
  detailsPanelWrapper: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  detailsRowsContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
  },
  // Timeline styles
  timelineContainer: {
    flex: 1,
    overflow: "auto",
    position: "relative",
    paddingRight: "5px",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
  timelineWrapper: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
}));

// Define stages
const stages = [
  "opportunity",
  "in_planning",
  "in_implementation",
  "in_capture",
  "concluded",
];

// Define status order for sorting within stages
const statusOrder = {
  red: 7,
  yellow: 6,
  green: 5,
  active: 4,
  on_hold: 3,
  cancelled: 2,
  finished: 1,
  gray: 0,
};

// Helper to get display status
const getInitiativeDisplayStatus = (initiative) => {
  if (!initiative) return "gray";
  const { status, follow_up } = initiative;
  if (status === "finished") return "green";
  if (status === "on_hold") return "yellow";
  if (status === "cancelled") return "red";
  if (status === "active" && follow_up && follow_up.length > 0) {
    const trafficLight = follow_up[0].traffic_light;
    if (trafficLight === 1) return "green";
    if (trafficLight === 2) return "yellow";
    if (trafficLight === 3) return "red";
  }
  return "gray";
};

// Helper function to get relative time
const getDateFromNow = (date) => {
  if (!date) return null;
  return moment.utc(date, "YYYY-MM-DD HH:mm").local().fromNow();
};

// eslint-disable-next-line no-unused-vars
const _mockInitiativesRemoved = {
  0: {
    id: 0,
    short_description: "placeholder",
    long_description:
      "Projeto para otimizar rotas de entrega e reduzir custos de frete.",
    leader: "João Silva",
    margin_benefit: 0.025,
    margin_benefit_global: 0.018,
    margin_benefit_ratio: 0.72,
    stage: "in_capture",
    status: "active",
    follow_up: [
      {
        traffic_light: 1,
        date: "2024-12-01 10:30",
        event_type: "update",
        description: "Iniciativa progredindo conforme planejado.",
        user_name: "João Silva",
      },
      {
        traffic_light: 2,
        date: "2024-11-15 14:00",
        event_type: "update",
        description: "Aguardando aprovação de orçamento adicional.",
        user_name: "Maria Santos",
      },
    ],
    margin_benefit_start: "2024-06",
    margin_benefit_end: "2025-06",
    project_start: "2024-03",
    pnl_impact_line: "logistics",
    tags: [
      { id: 1, name: "Logística" },
      { id: 2, name: "Custos" },
    ],
    scope: [
      { dimension: "Canal", values: ["Atacado", "Varejo"] },
      { dimension: "Região", values: ["Sul", "Sudeste"] },
    ],
  },
  2: {
    id: 2,
    short_description: "Otimização de mix de produtos",
    long_description: "Análise e reestruturação do portfólio de produtos.",
    leader: "Maria Santos",
    margin_benefit: 0.032,
    margin_benefit_global: 0.024,
    margin_benefit_ratio: 0.75,
    stage: "in_implementation",
    status: "active",
    follow_up: [
      {
        traffic_light: 2,
        date: "2024-11-15 16:00",
        event_type: "update",
        description: "Identificados produtos de baixa performance.",
        user_name: "Maria Santos",
      },
    ],
    margin_benefit_start: "2024-08",
    margin_benefit_end: "2025-08",
    project_start: "2024-06",
    pnl_impact_line: "gross_margin",
    tags: [{ id: 3, name: "Mix" }],
    scope: [],
  },
  3: {
    id: 3,
    short_description: "Renegociação de contratos",
    long_description: "Renegociação com fornecedores principais.",
    leader: "Carlos Oliveira",
    margin_benefit: 0.015,
    margin_benefit_global: 0.012,
    margin_benefit_ratio: 0.8,
    stage: "in_planning",
    status: "active",
    follow_up: [
      {
        traffic_light: 1,
        date: "2024-11-20 10:00",
        event_type: "update",
        description: "Mapeamento de contratos concluído.",
        user_name: "Carlos Oliveira",
      },
    ],
    margin_benefit_start: "2025-01",
    margin_benefit_end: "2025-12",
    project_start: "2024-10",
    pnl_impact_line: "cmv",
    tags: [],
    scope: [{ dimension: "Categoria", values: ["Matéria-prima"] }],
  },
  4: {
    id: 4,
    short_description: "Melhoria de processos produtivos",
    long_description: "",
    leader: "Ana Paula",
    margin_benefit: 0.028,
    margin_benefit_global: 0.021,
    margin_benefit_ratio: 0.75,
    stage: "opportunity",
    status: "active",
    follow_up: [],
    margin_benefit_start: "2025-03",
    margin_benefit_end: "2026-03",
    project_start: "2025-01",
    pnl_impact_line: null,
    tags: [],
    scope: [],
  },
  5: {
    id: 5,
    short_description: "Expansão canal digital",
    long_description: "Projeto concluído de expansão dos canais digitais.",
    leader: "Roberto Lima",
    margin_benefit: 0.045,
    margin_benefit_global: 0.036,
    margin_benefit_ratio: 0.8,
    stage: "concluded",
    status: "finished",
    follow_up: [
      {
        traffic_light: 1,
        date: "2024-09-30 17:00",
        event_type: "update",
        description: "Projeto finalizado com sucesso.",
        user_name: "Roberto Lima",
      },
    ],
    margin_benefit_start: "2024-01",
    margin_benefit_end: "2024-09",
    project_start: "2023-10",
    pnl_impact_line: "net_revenue",
    tags: [{ id: 4, name: "Digital" }],
    scope: [],
  },
  6: {
    id: 6,
    short_description: "Revisão de preços premium",
    long_description: "Análise e ajuste de preços para linha premium.",
    leader: "Fernanda Costa",
    margin_benefit: 0.022,
    margin_benefit_global: 0.017,
    margin_benefit_ratio: 0.77,
    stage: "in_capture",
    status: "active",
    follow_up: [
      {
        traffic_light: 3,
        date: "2024-10-20 09:00",
        event_type: "update",
        description: "Atraso na implementação.",
        user_name: "Fernanda Costa",
      },
    ],
    margin_benefit_start: "2024-09",
    margin_benefit_end: "2025-03",
    project_start: "2024-07",
    pnl_impact_line: "gross_revenue",
    tags: [{ id: 5, name: "Pricing" }],
    scope: [{ dimension: "Categoria", values: ["Premium"] }],
  },
  7: {
    id: 7,
    short_description: "Automação de processos",
    long_description: "",
    leader: "Pedro Souza",
    margin_benefit: 0.019,
    margin_benefit_global: 0.014,
    margin_benefit_ratio: 0.74,
    stage: "opportunity",
    status: "active",
    follow_up: [],
    margin_benefit_start: "2025-06",
    margin_benefit_end: "2026-06",
    project_start: "2025-03",
    pnl_impact_line: null,
    tags: [],
    scope: [],
  },
  8: {
    id: 8,
    short_description: "Programa de fidelidade B2B",
    long_description: "Criação de programa de fidelidade para clientes B2B.",
    leader: "Julia Mendes",
    margin_benefit: 0.038,
    margin_benefit_global: 0.03,
    margin_benefit_ratio: 0.79,
    stage: "in_implementation",
    status: "active",
    follow_up: [
      {
        traffic_light: 1,
        date: "2024-12-05 11:00",
        event_type: "update",
        description: "Desenvolvimento em andamento.",
        user_name: "Julia Mendes",
      },
    ],
    margin_benefit_start: "2025-02",
    margin_benefit_end: "2025-12",
    project_start: "2024-09",
    pnl_impact_line: "net_revenue",
    tags: [{ id: 6, name: "B2B" }],
    scope: [{ dimension: "Canal", values: ["Distribuidor"] }],
  },
};

// Helper component for stage numbers
const StageNumber = memo(({ label, value, grow = false }) => {
  const isPercentage = typeof value === "string" && value.includes("p.p");
  const displayValue = isPercentage ? value.replace(" p.p", "") : value;
  return (
    <div
      style={{
        flex: grow ? 1 : "unset",
        textAlign: "center",
        minWidth: "80px",
        width: "50%",
        margin: "2px 0 4px",
        color: "#555555",
      }}
    >
      <div
        style={{
          fontWeight: 300,
          fontSize: "0.75em",
          textTransform: "lowercase",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: "1.5em" }}>
        {displayValue}
        {isPercentage && <span style={{ fontSize: "0.6em" }}>{" p.p."}</span>}
      </div>
    </div>
  );
});

StageNumber.displayName = "StageNumber";

StageNumber.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  grow: PropTypes.bool,
};

// Notification helper
const notify = (message, type = "info") => {
  toast(message, {
    type,
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    transition: ToastSlide,
  });
};

// Read-only Initiative Details Panel (simplified layout matching original structure)
const InitiativeDetailsPanel = memo(({ initiative, t, clientColor }) => {
  const classes = useStyles();

  if (!initiative) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return moment(dateStr, "YYYY-MM").format("MMM/YYYY");
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return "-";
    return `${(value * 100).toFixed(2)}%`;
  };

  return (
    <div className={classes.detailsPanelWrapper}>
      {/* Header Card */}
      <Card
        className={classes.detailsCard}
        style={{
          height: "32px",
          flexShrink: 0,
          backgroundImage: `linear-gradient(to right, ${blendColors(clientColor, grayColor[6], 0.5)} , ${clientColor})`,
        }}
      >
        <CardHeader style={{ padding: "0 6px 10px 12px" }}>
          <p className={classes.cardCategory}>
            {t("initiatives.initiative_details")}
          </p>
        </CardHeader>
      </Card>

      {/* Rows Container - fills remaining space */}
      <div className={classes.detailsRowsContainer}>
        {/* Description Row */}
        <div className={classes.rowContainer}>
          <div className={classes.titleCard}>
            <Card style={{ height: "100%", margin: "0" }}>
              <div className={classes.titleCardInner}>
                {t("initiatives.description")}
              </div>
            </Card>
          </div>
          <div className={classes.contentCard}>
            <Card style={{ height: "100%", margin: "0" }}>
              <CardBody className={classes.contentCardBody}>
                <GridContainer spacing={1}>
                  <GridItem xs={8}>
                    <div className={classes.fieldLabel}>
                      {t("initiatives.initiative_name")}
                    </div>
                    <div
                      className={cx(classes.fieldValue, classes.fieldValueBold)}
                    >
                      {initiative.short_description || "-"}
                    </div>
                  </GridItem>
                  <GridItem xs={4}>
                    <div className={classes.fieldLabel}>
                      {t("initiatives.leader")}
                    </div>
                    <div className={classes.fieldValue}>
                      {initiative.leader || "-"}
                    </div>
                  </GridItem>
                </GridContainer>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Scope Row */}
        <div className={classes.rowContainer}>
          <div className={classes.titleCard}>
            <Card style={{ height: "100%", margin: "0" }}>
              <div className={classes.titleCardInner}>
                {t("initiatives.scope")}
              </div>
            </Card>
          </div>
          <div className={classes.contentCard}>
            <Card style={{ height: "100%", margin: "0" }}>
              <CardBody className={classes.contentCardBody}>
                {initiative.scope && initiative.scope.length > 0 ? (
                  initiative.scope.map((s, idx) => {
                    // Support both formats:
                    // Mock: { dimension, values: [] }
                    // API:  { filters: { dim: [items] }, filters_sequence: [] }
                    if (s.dimension && s.values) {
                      return (
                        <span key={idx} className={classes.scopeChip}>
                          {s.dimension}: {s.values.join(", ")}
                        </span>
                      );
                    }
                    if (s.filters) {
                      const entries = Object.entries(s.filters).filter(
                        ([, v]) => v && v.length > 0,
                      );
                      if (entries.length === 0) return null;
                      return entries.map(([dim, vals]) => (
                        <span
                          key={`${idx}-${dim}`}
                          className={classes.scopeChip}
                        >
                          {dim}: {Array.isArray(vals) ? vals.join(", ") : vals}
                        </span>
                      ));
                    }
                    return null;
                  })
                ) : (
                  <span className={classes.noValueText}>
                    {t("initiatives.no_filters_general_scope")}
                  </span>
                )}
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Benefit Row */}
        <div className={classes.rowContainer}>
          <div className={classes.titleCard}>
            <Card style={{ height: "100%", margin: "0" }}>
              <div className={classes.titleCardInner}>
                {t("initiatives.benefit")}
              </div>
            </Card>
          </div>
          <div className={classes.contentCard}>
            <Card style={{ height: "100%", margin: "0" }}>
              <CardBody className={classes.contentCardBody}>
                <GridContainer spacing={1}>
                  <GridItem xs={4}>
                    <div className={classes.fieldLabel}>
                      {t("initiatives.pnl_impact_line")}
                    </div>
                    <div className={classes.fieldValue}>
                      {initiative.pnl_impact_line || "-"}
                    </div>
                  </GridItem>
                  <GridItem xs={2}>
                    <div className={classes.fieldLabel}>
                      {t("initiatives.benefit")}
                    </div>
                    <div className={classes.fieldValue}>
                      {formatPercent(initiative.margin_benefit)}
                    </div>
                  </GridItem>
                  <GridItem xs={3}>
                    <div className={classes.fieldLabel}>
                      {t("initiatives.scope_margin_ratio")}
                    </div>
                    <div className={classes.fieldValue}>
                      × {formatPercent(initiative.margin_benefit_ratio)}
                    </div>
                  </GridItem>
                  <GridItem xs={3}>
                    <div className={classes.fieldLabel}>
                      {t("initiatives.benefit_global")}
                    </div>
                    <div
                      className={cx(classes.fieldValue, classes.fieldValueBold)}
                    >
                      = {formatPercent(initiative.margin_benefit_global)}
                    </div>
                  </GridItem>
                </GridContainer>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Dates Row */}
        <div className={classes.rowContainer}>
          <div className={classes.titleCard}>
            <Card style={{ height: "100%", margin: "0" }}>
              <div className={classes.titleCardInner}>
                {t("initiatives.dates")}
              </div>
            </Card>
          </div>
          <div className={classes.contentCard}>
            <Card style={{ height: "100%", margin: "0" }}>
              <CardBody className={classes.contentCardBody}>
                <GridContainer spacing={1}>
                  <GridItem xs={4}>
                    <div className={classes.fieldLabel}>
                      {t("initiatives.project_start")}
                    </div>
                    <div className={classes.fieldValue}>
                      {formatDate(initiative.project_start)}
                    </div>
                  </GridItem>
                  <GridItem xs={4}>
                    <div className={classes.fieldLabel}>
                      {t("initiatives.capture_start")}
                    </div>
                    <div className={classes.fieldValue}>
                      {formatDate(initiative.margin_benefit_start)}
                    </div>
                  </GridItem>
                  <GridItem xs={4}>
                    <div className={classes.fieldLabel}>
                      {t("initiatives.capture_full_potential")}
                    </div>
                    <div className={classes.fieldValue}>
                      {formatDate(initiative.margin_benefit_end)}
                    </div>
                  </GridItem>
                </GridContainer>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Tags Row */}
        <div className={classes.rowContainer}>
          <div className={classes.titleCard}>
            <Card style={{ height: "100%", margin: "0" }}>
              <div className={classes.titleCardInner}>
                {t("initiatives.tags")}
              </div>
            </Card>
          </div>
          <div className={classes.contentCard}>
            <Card style={{ height: "100%", margin: "0" }}>
              <CardBody className={classes.contentCardBody}>
                {initiative.tags && initiative.tags.length > 0 ? (
                  initiative.tags.map((tag) => (
                    <span key={tag.id} className={classes.tagChip}>
                      {tag.name}
                    </span>
                  ))
                ) : (
                  <span className={classes.noValueText}>-</span>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
});

InitiativeDetailsPanel.displayName = "InitiativeDetailsPanel";

InitiativeDetailsPanel.propTypes = {
  initiative: PropTypes.object,
  t: PropTypes.func.isRequired,
  clientColor: PropTypes.string.isRequired,
};

// Initiative Execution Panel (Timeline + Add Update button, no Stage block)
const InitiativeExecutionPanel = memo(
  ({ initiative, t, clientColor, onAddUpdate }) => {
    const classes = useStyles();
    const timelineContainerRef = useRef(null);

    // Get timeline stories from follow_up
    const timelineStories = useMemo(() => {
      if (!initiative || !initiative.follow_up) return [];

      return initiative.follow_up.map((fup) => ({
        inverted: true,
        badgeColor: "client",
        badgeIcon:
          fup.event_type === "creation" ? OpenInBrowser : DirectionsWalk,
        title: t("initiatives.follow_up." + fup.event_type),
        titleColor: "client",
        trafficLight: fup.traffic_light,
        body: (
          <p style={{ fontSize: "14px", lineHeight: "1.3", margin: "0" }}>
            {fup.description}
          </p>
        ),
        footerTitle: (
          <div style={{ fontSize: "11px" }}>
            <span style={{ color: grayColor[1] }}>{fup.user_name}</span>
            {" — "}
            <span style={{ color: grayColor[1] }}>
              {getDateFromNow(fup.date)}
            </span>
          </div>
        ),
      }));
    }, [initiative, t]);

    const canAddUpdate =
      initiative &&
      initiative.status !== "cancelled" &&
      initiative.status !== "finished";

    return (
      <div className={classes.detailsPanelWrapper}>
        {/* Header Card */}
        <Card
          className={classes.detailsCard}
          style={{
            height: "32px",
            flexShrink: 0,
            backgroundImage: `linear-gradient(to right, ${blendColors(clientColor, grayColor[6], 0.5)} , ${clientColor})`,
          }}
        >
          <CardHeader style={{ padding: "0 6px 10px 12px" }}>
            <GridContainer>
              <GridItem>
                <p className={classes.cardCategory}>
                  {t("initiatives.initiative_execution")}
                </p>
              </GridItem>
              <GridItem xs={true}></GridItem>
              <GridItem>
                <Tooltip
                  placement="top"
                  title={t("initiatives.add_update")}
                  PopperProps={{ style: { marginBottom: "-8px" } }}
                >
                  <span>
                    <Button
                      justIcon
                      color="client"
                      className={cx(classes.cardButton, {
                        [classes.cardButtonDisabled]: !canAddUpdate,
                      })}
                      onClick={onAddUpdate}
                      disabled={!canAddUpdate}
                    >
                      <div style={{ width: "100%" }}>
                        <PostAdd className={classes.cardButtonIcon} />
                      </div>
                    </Button>
                  </span>
                </Tooltip>
              </GridItem>
            </GridContainer>
          </CardHeader>
        </Card>

        {/* Timeline */}
        <div className={classes.timelineWrapper}>
          <div ref={timelineContainerRef} className={classes.timelineContainer}>
            {timelineStories.length === 0 ? (
              <div
                style={{
                  fontSize: "12px",
                  textAlign: "center",
                  padding: "20px",
                  color: grayColor[5],
                }}
              >
                {t("initiatives.no_updates")}
              </div>
            ) : (
              <InitiativesTimeline simple stories={timelineStories} />
            )}
          </div>
        </div>
      </div>
    );
  },
);

InitiativeExecutionPanel.displayName = "InitiativeExecutionPanel";

InitiativeExecutionPanel.propTypes = {
  initiative: PropTypes.object,
  t: PropTypes.func.isRequired,
  clientColor: PropTypes.string.isRequired,
  onAddUpdate: PropTypes.func.isRequired,
};

function InitiativesSection({
  periodReference: _periodReference,
  clientColor: propsClientColor,
  dataOrchestrator,
  focusMode = false,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { clientColor: themeClientColor } = useThemeColors();

  void _periodReference;
  const clientColor = propsClientColor || themeClientColor;

  // Get initiatives from orchestrator (real data from action_plans endpoint)
  const rawInitiatives = dataOrchestrator?.initiativesData?.initiatives;

  // Build initiatives map from API response
  const [initiatives, setInitiatives] = useState({});

  // Sync from orchestrator when data arrives
  useEffect(() => {
    if (rawInitiatives && Object.keys(rawInitiatives).length > 0) {
      setInitiatives(rawInitiatives);
    }
  }, [rawInitiatives]);
  const [selectedInitiative, setSelectedInitiative] = useState(null);
  const [loadingInitiativeId, setLoadingInitiativeId] = useState(null);
  const [newFilters] = useState({ stage: null });
  const [addUpdateModalOpen, setAddUpdateModalOpen] = useState(false);
  const [initiativesBusy, setInitiativesBusy] = useState(false);

  // State for key messages (will be replaced by API data)
  const [keyMessages, setKeyMessages] = useState([]);

  // Process initiatives with display status
  const processedInitiatives = useMemo(() => {
    return Object.entries(initiatives).map(([key, init]) => ({
      ...init,
      id: init.id || key, // API uses UUID keys; mock used numeric ids
      initiative: init.short_description,
      displayStatus: getInitiativeDisplayStatus(init),
    }));
  }, [initiatives]);

  // Calculate stage summaries
  const stageSummary = useMemo(() => {
    const summary = {};
    stages.forEach((stage) => {
      const stageInitiatives = processedInitiatives.filter(
        (init) => init.stage === stage,
      );
      summary[stage] = {
        count: stageInitiatives.length,
        global_benefit: stageInitiatives.reduce(
          (sum, init) => sum + init.margin_benefit_global * 100,
          0,
        ),
      };
    });
    return summary;
  }, [processedInitiatives]);

  // Get selected initiative data
  const selectedInitiativeData = useMemo(() => {
    if (!selectedInitiative) return null;
    return initiatives[selectedInitiative] || null;
  }, [selectedInitiative, initiatives]);

  // Handle initiative click (toggle selection)
  const handleInitiativeClick = useCallback((initiativeId) => {
    setSelectedInitiative((prev) =>
      prev === initiativeId ? null : initiativeId,
    );
  }, []);

  // Handle add update - open the modal
  const handleAddUpdate = useCallback(() => {
    if (selectedInitiative) {
      setAddUpdateModalOpen(true);
    }
  }, [selectedInitiative]);

  // Save add update via real API
  const saveAddUpdate = useCallback(
    (updateText, trafficLight = 1) => {
      if (!selectedInitiative) return;

      setInitiativesBusy(true);

      client
        .post("api/initiatives_add_update/", {
          initiative_id: selectedInitiative,
          description: updateText,
          traffic_light: trafficLight,
          event_type: "update",
        })
        .then(() => {
          // Add locally for immediate feedback
          setInitiatives((prev) => {
            const updated = { ...prev };
            if (updated[selectedInitiative]) {
              const newUpdate = {
                traffic_light: trafficLight,
                date: moment().format("YYYY-MM-DD HH:mm"),
                event_type: "update",
                description: updateText,
                user_name: "Eu",
              };
              updated[selectedInitiative] = {
                ...updated[selectedInitiative],
                follow_up: [
                  newUpdate,
                  ...(updated[selectedInitiative].follow_up || []),
                ],
              };
            }
            return updated;
          });
          setAddUpdateModalOpen(false);
          notify(t("initiatives.update_added_successfully"), "success");
        })
        .catch(() => {
          notify(t("initiatives.error_adding_update"), "error");
        })
        .finally(() => {
          setInitiativesBusy(false);
        });
    },
    [selectedInitiative, t],
  );

  // Drag and drop handler — updates stage via API
  const onDragEnd = useCallback(
    (result) => {
      const { destination, source, draggableId } = result;

      if (!destination) return;
      if (destination.droppableId === source.droppableId) return;

      const initiativeId = draggableId;
      const newStage = destination.droppableId;

      // Defer state update: requestAnimationFrame ensures dnd animation completes
      requestAnimationFrame(() => {
        setInitiatives((prev) => {
          const updated = { ...prev };
          if (updated[initiativeId]) {
            updated[initiativeId] = {
              ...updated[initiativeId],
              stage: newStage,
            };
          }
          return updated;
        });

        setLoadingInitiativeId(initiativeId);

        client
          .post("api/initiatives_update_stage/", {
            initiative_id: initiativeId,
            stage: newStage,
          })
          .then((response) => {
            if (response.data.success) {
              notify(t("initiatives.stage_updated_successfully"), "success");
            } else {
              setInitiatives((prev) => {
                const reverted = { ...prev };
                if (reverted[initiativeId]) {
                  reverted[initiativeId] = {
                    ...reverted[initiativeId],
                    stage: source.droppableId,
                  };
                }
                return reverted;
              });
              notify(t("initiatives.stage_update_failed"), "error");
            }
          })
          .catch((error) => {
            console.error("Stage update error:", error);
            setInitiatives((prev) => {
              const reverted = { ...prev };
              if (reverted[initiativeId]) {
                reverted[initiativeId] = {
                  ...reverted[initiativeId],
                  stage: source.droppableId,
                };
              }
              return reverted;
            });
            notify(t("initiatives.stage_update_failed"), "error");
          })
          .finally(() => {
            setLoadingInitiativeId(null);
          });
      });
    },
    [t],
  );

  return (
    <FocusModeWrapper focusMode={focusMode}>
      <div
        className={
          focusMode ? classes.sectionContainerFocus : classes.sectionContainer
        }
        style={focusMode ? { height: "100%", marginTop: 0 } : {}}
      >
        <Card className={focusMode ? classes.cardFocus : classes.card}>
          <CardBody className={classes.cardBody}>
            {/* Kanban Board - 40% height */}
            <div className={classes.boardContainer}>
              <DragDropContext onDragEnd={onDragEnd}>
                <div className={classes.boardGrid}>
                  {stages.map((stageKey) => {
                    const stageItems = processedInitiatives
                      .filter((initiative) => initiative.stage === stageKey)
                      .sort((a, b) => {
                        const aStatus = statusOrder[a.displayStatus] || 0;
                        const bStatus = statusOrder[b.displayStatus] || 0;
                        return bStatus - aStatus;
                      });

                    return (
                      <DroppableStageColumn
                        key={stageKey}
                        stageKey={stageKey}
                        stageItems={stageItems}
                        stageSummary={stageSummary}
                        newFilters={newFilters}
                        statusOrder={statusOrder}
                        loadingInitiativeId={loadingInitiativeId}
                        handleInitiativeClick={handleInitiativeClick}
                        t={t}
                        StageNumber={StageNumber}
                        selectedInitiativeId={selectedInitiative}
                      />
                    );
                  })}
                </div>
              </DragDropContext>
            </div>

            {/* Details Area - 60% height */}
            <div className={classes.detailsContainer}>
              {selectedInitiativeData ? (
                <div className={classes.detailsContent}>
                  {/* Back button - bottom right */}
                  <div
                    style={{
                      position: "absolute",
                      bottom: "8px",
                      right: "12px",
                      zIndex: 2,
                      cursor: "pointer",
                      fontSize: "12px",
                      color: "#888",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                    onClick={() => setSelectedInitiative(null)}
                  >
                    ← Voltar
                  </div>

                  {/* Left Panel - Initiative Details (read-only) */}
                  <div className={classes.detailsLeftPanel}>
                    <InitiativeDetailsPanel
                      initiative={selectedInitiativeData}
                      t={t}
                      clientColor={clientColor}
                    />
                  </div>

                  {/* Right Panel - Initiative Execution (timeline + add update) */}
                  <div className={classes.detailsRightPanel}>
                    <InitiativeExecutionPanel
                      initiative={selectedInitiativeData}
                      t={t}
                      clientColor={clientColor}
                      onAddUpdate={handleAddUpdate}
                    />
                  </div>
                </div>
              ) : (
                <div className={classes.noSelectionMessage}>
                  {t("initiatives.select_initiative_to_view_details")}
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Add Update Dialog */}
        {selectedInitiative && (
          <AddUpdateDialog
            addUpdateModalOpen={addUpdateModalOpen}
            setAddUpdateModalOpen={setAddUpdateModalOpen}
            initiatives={initiatives}
            selectedInitiative={selectedInitiative}
            initiativesBusy={initiativesBusy}
            saveAddUpdate={saveAddUpdate}
            classes={classes}
            t={t}
            clientColor={clientColor}
          />
        )}

        {/* Key Messages Panel */}
        <KeyMessagesPanel
          messages={keyMessages}
          onMessagesChange={setKeyMessages}
        />
      </div>
    </FocusModeWrapper>
  );
}

InitiativesSection.propTypes = {
  periodReference: PropTypes.string,
  clientColor: PropTypes.string,
  dataOrchestrator: PropTypes.object,
  focusMode: PropTypes.bool,
};

InitiativesSection.defaultProps = {
  periodReference: "ytd",
  clientColor: null,
  dataOrchestrator: null,
  focusMode: false,
};

export default memo(InitiativesSection);
